/**
 * Boli Live - Product Management Page
 *
 * Product list/table view for sellers to manage their inventory.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Package,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Pause,
  Play,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { getProductService } from '@boli/core';
import type { Product, ProductStatus } from '@boli/types';
import { cn, formatPKR } from '../lib/utils';
import { useAuthStore } from '../lib/stores/auth-store';

const STATUS_FILTERS: { value: ProductStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Products' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'paused', label: 'Paused' },
  { value: 'sold_out', label: 'Sold Out' },
  { value: 'archived', label: 'Archived' },
];

const STATUS_COLORS: Record<ProductStatus, { bg: string; text: string }> = {
  draft: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-300' },
  active: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
  paused: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
  sold_out: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
  archived: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-500 dark:text-gray-400' },
};

export default function ProductManagementPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProductStatus | 'all'>('all');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      if (!user?.id) return;

      setIsLoading(true);
      setError(null);

      try {
        const productService = getProductService();
        const result = await productService.getProducts({ sellerId: user.id });
        setProducts(result.products);
      } catch {
        setError('Failed to load products');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [user?.id]);

  // Filter products
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      searchQuery === '' ||
      product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || product.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Toggle product status
  const toggleStatus = async (product: Product) => {
    const newStatus: ProductStatus = product.status === 'active' ? 'paused' : 'active';

    setActionLoading(product.id);
    try {
      const productService = getProductService();
      const result = await productService.updateProduct(product.id, { status: newStatus });

      if (result.success) {
        setProducts((prev) =>
          prev.map((p) => (p.id === product.id ? { ...p, status: newStatus } : p))
        );
      }
    } catch {
      // Toggle status error handled silently
    } finally {
      setActionLoading(null);
      setActiveDropdown(null);
    }
  };

  // Delete product
  const deleteProduct = async (product: Product) => {
    if (!confirm(`Are you sure you want to delete "${product.title}"?`)) return;

    setActionLoading(product.id);
    try {
      const productService = getProductService();
      const result = await productService.deleteProduct(product.id);

      if (result.success) {
        setProducts((prev) => prev.filter((p) => p.id !== product.id));
      }
    } catch {
      // Delete product error handled silently
    } finally {
      setActionLoading(null);
      setActiveDropdown(null);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Stats
  const totalProducts = products.length;
  const activeProducts = products.filter((p) => p.status === 'active').length;
  const draftProducts = products.filter((p) => p.status === 'draft').length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-secondary/30 pb-24"
    >
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-foreground">Products</h1>
              <p className="text-sm text-muted-foreground">
                Manage your fixed-price listings
              </p>
            </div>
            <Link
              to="/seller/products/new"
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </Link>
          </div>

          {/* Stats Row */}
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Total:</span>
              <span className="font-semibold text-foreground">{totalProducts}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Active:</span>
              <span className="font-semibold text-green-600">{activeProducts}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Draft:</span>
              <span className="font-semibold text-gray-600">{draftProducts}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4">
        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ProductStatus | 'all')}
              className="pl-10 pr-8 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none"
            >
              {STATUS_FILTERS.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Error</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredProducts.length === 0 && (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Package className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            {products.length === 0 ? (
              <>
                <h2 className="text-xl font-semibold text-foreground mb-2">No products yet</h2>
                <p className="text-muted-foreground mb-6">
                  Start adding products to your store to sell fixed-price items.
                </p>
                <Link
                  to="/seller/products/new"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Your First Product
                </Link>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-foreground mb-2">No matching products</h2>
                <p className="text-muted-foreground">
                  Try adjusting your search or filter criteria.
                </p>
              </>
            )}
          </div>
        )}

        {/* Products Table */}
        {!isLoading && !error && filteredProducts.length > 0 && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                      Product
                    </th>
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                      Price
                    </th>
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                      Stock
                    </th>
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                      Status
                    </th>
                    <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr
                      key={product.id}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                            <img
                              src={product.images?.[0] || '/placeholder-product.jpg'}
                              alt={product.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate max-w-xs">
                              {product.title}
                            </p>
                            {product.sku && (
                              <p className="text-xs text-muted-foreground">SKU: {product.sku}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-foreground">
                          {formatPKR(product.price)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'font-medium',
                            product.stock === 0 ? 'text-red-600' : 'text-foreground'
                          )}
                        >
                          {product.stock}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wide',
                            STATUS_COLORS[product.status].bg,
                            STATUS_COLORS[product.status].text
                          )}
                        >
                          {product.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/seller/products/${product.id}/edit`)}
                            className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdown(
                                  activeDropdown === product.id ? null : product.id
                                );
                              }}
                              className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {activeDropdown === product.id && (
                              <div
                                className="absolute right-0 top-full mt-1 w-40 bg-card border border-border rounded-md shadow-lg z-10"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {(product.status === 'active' || product.status === 'paused') && (
                                  <button
                                    onClick={() => toggleStatus(product)}
                                    disabled={actionLoading === product.id}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                                  >
                                    {product.status === 'active' ? (
                                      <>
                                        <Pause className="w-4 h-4" />
                                        Pause Listing
                                      </>
                                    ) : (
                                      <>
                                        <Play className="w-4 h-4" />
                                        Activate Listing
                                      </>
                                    )}
                                  </button>
                                )}
                                <button
                                  onClick={() => deleteProduct(product)}
                                  disabled={actionLoading === product.id}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-border">
              {filteredProducts.map((product) => (
                <div key={product.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-16 h-16 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                      <img
                        src={product.images?.[0] || '/placeholder-product.jpg'}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-foreground line-clamp-2">
                            {product.title}
                          </p>
                          <p className="text-sm font-semibold text-foreground mt-1">
                            {formatPKR(product.price)}
                          </p>
                        </div>
                        <span
                          className={cn(
                            'text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0',
                            STATUS_COLORS[product.status].bg,
                            STATUS_COLORS[product.status].text
                          )}
                        >
                          {product.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span>Stock: {product.stock}</span>
                        {product.sku && <span>SKU: {product.sku}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                    <button
                      onClick={() => navigate(`/seller/products/${product.id}/edit`)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    {(product.status === 'active' || product.status === 'paused') && (
                      <button
                        onClick={() => toggleStatus(product)}
                        disabled={actionLoading === product.id}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50"
                      >
                        {product.status === 'active' ? (
                          <>
                            <Pause className="w-4 h-4" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4" />
                            Activate
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => deleteProduct(product)}
                      disabled={actionLoading === product.id}
                      className="px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
